import { Injectable } from '@angular/core';
import { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { mergeMap, tap, retryWhen, map } from 'rxjs/operators';

import { AUTH_CONSTANTS } from '@app/shared/constants';
import { RouteHelperService } from '@app/shared/services/route-helper.service';
import { AuthService } from '@app/api/api-services/auth.service';
import { EidAuthService } from '@app/auth/services/eid-auth.service';
import { ConfigService } from '@app/configuration/config.service';
import { LogoutMode } from '@app/auth/enums/logout-mode.enum';
import { TokenStorageService } from '@shared/services';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  // Static endpoints that must bypass auth regardless of config load state
  private readonly staticAnonymousEndpoints = [
    'api/auth/',
    'api/active-directory/',
    'api/app-settings/get-userway-status',
    'assets/icons',
    'assets/environment.json'
  ];

  // Endpoints that should not trigger logout on 401/403 (e.g. third-party services with their own auth)
  private readonly noLogoutOnUnauthorizedEndpoints = ['v1/writing-assistant'];
  // Endpoints authenticated with the EID access token instead of the PowerFTO token
  private readonly eidTokenEndpoints = ['v1/writing-assistant'];
  private readonly importantEndpoints = ['api/auth/logout'];

  // Computed lazily so config values are resolved at request time (not construction time)
  private get anonymousEndpoints(): string[] {
    const config = this.configService.getConfig();
    return [
      ...this.staticAnonymousEndpoints,
      ...(config ? [config.authSettings.authServerUrl, config.powerDMSGraphqlBaseURL] : [])
    ];
  }

  constructor(
    private readonly auth: AuthService,
    private readonly authService: AuthService,
    private readonly routeHelper: RouteHelperService,
    private readonly tokenStorageService: TokenStorageService,
    private readonly configService: ConfigService,
    private readonly eidAuthService: EidAuthService
  ) {}

  public intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (this.usesEidToken(request)) {
      return this.eidAuthService.accessToken.pipe(
        mergeMap((token) =>
          this.handleResponseError(
            token
              ? request.clone({ setHeaders: { Authorization: `${AUTH_CONSTANTS.AUTH_PREFIX} ${token}` } })
              : request,
            next
          )
        )
      );
    }

    if (this.skipAuthorization(request)) {
      return this.handleResponseError(request, next);
    }

    return this.auth.getToken().pipe(
      mergeMap((token) => {
        request = request.clone({
          setHeaders: {
            Authorization: `${AUTH_CONSTANTS.AUTH_PREFIX} ${token}`
          }
        });

        return this.handleResponseError(request, next);
      })
    );
  }

  private usesEidToken(request: HttpRequest<any>): boolean {
    return this.eidTokenEndpoints.some((x) => request.url.includes(x));
  }

  private skipAuthorization(request: HttpRequest<any>): boolean {
    return (
      !this.importantEndpoints.some((x) => request.url.includes(x)) &&
      this.anonymousEndpoints.some((x) => request.url.includes(x))
    );
  }

  private handleResponseError(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next
      .handle(request)
      .pipe(
        retryWhen((errors) =>
          errors.pipe(
            map((error: HttpErrorResponse) => {
              if (request.wasFailedBefore || (error.status !== 401 && error.status !== 403)) {
                throw error;
              }

              request.wasFailedBefore = true;

              return error;
            })
          )
        )
      )
      .pipe(
        tap({
          error: (error) => {
            const isPowerDmsSSOEnabled =
              this.tokenStorageService.userInfo &&
              this.tokenStorageService.userInfo.CurrentSite.PowerSuitEidAuthEnabled;

            if (
              error instanceof HttpErrorResponse &&
              (error.status === 401 || error.status === 403) &&
              !this.anonymousEndpoints.some((x) => request.url.includes(x)) &&
              !this.noLogoutOnUnauthorizedEndpoints.some((x) => request.url.includes(x))
            ) {
              this.authService.logout(LogoutMode.Manual, true).subscribe(
                () => {
                  if (!isPowerDmsSSOEnabled) {
                    this.routeHelper.login();
                  }
                },
                () => this.routeHelper.login()
              );
            }
          }
        })
      );
  }
}
